import { APIService } from 'src/app/service/api.service';
import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import dayjs from '../config/dayjs.config';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { Theme } from 'src/app/model/theme';
import {
  Amenity,
  HashtagSearchDto,
  ProductConfirm,
  Ticket,
  TicketBadge,
  TicketCreateBulkDto,
  TicketSearchDto,
} from 'src/app/model/product';
import { Coupon } from '../model/coupon';
import { Pagination, PaginationSearchDto } from '../model/pagination';
import {
  Hashtag,
  Product,
  ProductArchive,
  ProductConfirmCreateDto,
  ProductCreateDto,
  ProductManageDto,
  ProductSearchDto,
  ProductStatus,
  ProductUpdateDto,
} from '../model/product';
import { TicketGroup } from './../model/product';
import { failIfSwallowed } from './../util/swallowed-http';
import { createArchiveRequestBody, parseArchiveVersion } from './../util/product-archive';

/** 어드민 쿠폰 지정 UI 후보 한 장. `GET api/products/:id/discount-coupon-candidates` 응답과 같다(NNB-912). */
export interface DiscountCouponCandidate {
  id: number;
  name: string;
  eligible: boolean;
  /** 지금 지정하면 될 적용가. 적격이 아니면 null */
  estimatedPrice: number | null;
  designated: boolean;
}

export interface DiscountCouponCandidates {
  candidates: DiscountCouponCandidate[];
  /** "지정 안 함(자동)" 을 고르면 될 가격 */
  autoPrice: number | null;
}

@Injectable({
  providedIn: 'root',
})
export class ProductService {
  /** 테마 목록 캐시. `searchTheme()` 참고 — 실패 시 참조를 비워 다음 호출이 재시도한다. */
  private themesCache$?: Observable<Theme[]>;

  constructor(private apiService: APIService) {}

  // sortOrder는 엔티티·DB에서 이미 제거됐지만, 구버전 NestJS는 이 필드를 필수로 요구하고
  // 받은 값을 DB 컬럼(아직 드랍 전)에 그대로 저장한다. nnb-angular·nnb-nestjs는 별개
  // blue-green 파이프라인이라 Angular가 먼저 배포돼도 생성·저장이 필수값 검증에 걸리지
  // 않도록 값을 실어 보낸다. 다음 정리 PR에서 NestJS 쪽 하위호환 필드 제거와 함께 뺄 것.
  addProduct(dto: ProductCreateDto): Observable<Product> {
    // 신규 생성은 기존 값이 없으므로 0 고정 — 덮어쓸 레거시 값 자체가 없다.
    return this.apiService.post<Product>(`/api/products`, { ...dto, sortOrder: 0 });
  }

  // sortOrder는 getRefinedProduct 응답에만 실려 온다. 호출부가 로드한 값을 그대로 넘겨야
  // 한다 — 0을 고정으로 보내면 비영(非零) 레거시 값이 저장 때마다 0으로 덮어써진다.
  updateProduct(id: number, dto: ProductUpdateDto, sortOrder?: number | null): Observable<Product> {
    return this.apiService.put<Product>(`/api/products/${id}`, { ...dto, sortOrder: sortOrder ?? 0 });
  }

  requestReview(id: number): Observable<Product> {
    return this.apiService.post<Product>(`/api/products/${id}/request-review`, {});
  }

  cancelReview(id: number): Observable<Product> {
    return this.apiService.post<Product>(`/api/products/${id}/cancel-review`, {});
  }

  manageProduct(id: number, dto: ProductManageDto) {
    return this.apiService.put(`/api/products/management/${id}`, dto);
  }

  /** 지금 이 상품에 지정할 수 있는 쿠폰 후보 — "숨은 ref 쿠폰" 포함, 적격 여부·적용가 계산은 서버가 한다(NNB-912). */
  getDiscountCouponCandidates(productId: number): Observable<DiscountCouponCandidates> {
    return this.apiService.get<DiscountCouponCandidates>(`/api/products/${productId}/discount-coupon-candidates`);
  }

  getRefinedProduct(
    productId: number,
    userId?: number,
    ref?: string,
    options?: { forceKorean?: boolean },
  ): Observable<Product> {
    // product를 가져온 후 ref 파라미터를 저장
    // forceKorean: admin 편집 시 원본(한국어) 콘텐츠 강제
    const langParam = options?.forceKorean ? '?lang=ko' : '';
    const product$ = userId
      ? this.apiService.get<Product>(`/api/products/product/refined/${productId}/user/${userId}${langParam}`)
      : this.apiService.get<Product>(`/api/products/product/refined/${productId}${langParam}`);

    // product에 ref 값을 저장하여 나중에 쿠폰 조회 시 사용
    return product$.pipe(
      tap((product) => {
        if (ref) {
          (product as any).__ref = ref;
        }
      }),
    );
  }

  // TODO: 점진적으로 getRefinedProduct로 변경해야함 또는 order, payment가 있어야하는 곳에서만 사용해야함
  getProduct(productId: number, includeOld?: boolean): Observable<Product> {
    return this.apiService.get<Product>(`/api/products/product/${productId}?includeOld=${includeOld}`);
  }

  getProducts(productIds: number[]): Observable<Product[]> {
    let params = new HttpParams();
    if (productIds?.length > 0) {
      productIds.forEach((id) => {
        params = params.append('productIds', id.toString());
      });
    }
    return this.apiService.get<Product[]>(`/api/products`, params);
  }

  addViewCount(productId: number, userId?: number): Observable<any> {
    return this.apiService.post<any>(`/api/products/product/viewCount`, {
      productId,
      userId,
    });
  }

  getProductWithAllTickets(
    productId: number,
    options?: { includeOld?: boolean; includeHidden?: boolean },
  ): Observable<Product> {
    const params = new URLSearchParams();
    if (options?.includeOld !== undefined) {
      params.append('includeOld', String(options.includeOld));
    }
    if (options?.includeHidden !== undefined) {
      params.append('includeHidden', String(options.includeHidden));
    }
    const queryString = params.toString();
    const url = `/api/products/product/with-all-tickets/${productId}${queryString ? `?${queryString}` : ''}`;
    return this.apiService.get<Product>(url);
  }

  search(search: ProductSearchDto): Observable<Pagination<Product>> {
    // Use GET with query parameters for searching
    const queryString = this.getProductSearchQuery(search);
    return this.apiService.get<Pagination<Product>>(`/api/products/search?${queryString}`);
  }

  private getProductSearchQuery(search: ProductSearchDto): string {
    const params = new URLSearchParams();

    // Add all search parameters to URL params
    if (search.page !== undefined) params.append('page', search.page.toString());
    if (search.limit !== undefined) params.append('limit', search.limit.toString());
    if (search.status !== undefined) params.append('status', search.status.toString());
    if (search.statusList?.length) {
      search.statusList.forEach((status) => params.append('statusList', status.toString()));
    }
    if (search.hostId !== undefined) params.append('hostId', search.hostId.toString());
    if (search.userId !== undefined) params.append('userId', search.userId.toString());
    if (search.representationCategories?.length) {
      search.representationCategories.forEach((cat) => params.append('representationCategories', cat));
    }
    if (search.hashtags?.length) {
      search.hashtags.forEach((tag) => params.append('hashtags', tag));
    }
    if (search.analysisHashtags?.length) {
      search.analysisHashtags.forEach((tag) => params.append('analysisHashtags', tag));
    }
    if (search.recentlyOpenDays !== undefined) params.append('recentlyOpenDays', search.recentlyOpenDays.toString());
    if (search.searchTerm) params.append('searchTerm', search.searchTerm);
    if (search.isBusiness !== undefined) params.append('isBusiness', search.isBusiness.toString());
    if (search.productIds?.length) {
      search.productIds.forEach((id) => params.append('productIds', id.toString()));
    }
    if (search.sidos?.length) {
      search.sidos.forEach((sido) => params.append('sidos', sido));
    }
    if (search.orderBy) params.append('orderBy', search.orderBy);

    return params.toString();
  }

  searchAdmin(term: ProductSearchDto): Observable<Pagination<Product>> {
    const limit = term.limit;
    const page = term.page;
    // Use statusList[0] if available, otherwise fall back to status
    const status = term.statusList?.[0] ?? term.status;

    // productIds가 있으면 공개 search 엔드포인트로 조회
    if (term.productIds && term.productIds.length > 0) {
      return this.apiService.get<Pagination<Product>>(`/api/products/search`, term);
    }

    // 쿼리스트링을 직접 조립하면 파라미터를 추가할 때마다 누락되기 쉽고 검색어 인코딩도 깨진다.
    // APIService 가 undefined/null 을 걸러내고 인코딩까지 처리하므로 객체로 넘긴다.
    return this.apiService.get<Pagination<Product>>('/api/products/search/admin', {
      searchTerm: term.searchTerm || '',
      limit,
      page,
      status: status ?? ProductStatus.ALL,
      businessGroupId: term.businessGroupId,
    });
  }

  deleteProduct(id: number) {
    return this.apiService.delete(`/api/products/${id}`);
  }

  getLikeProducts(search: PaginationSearchDto): Observable<Pagination<Product>> {
    return this.apiService.get<Pagination<Product>>(`/api/products/likes?page=${search.page}&limit=${search.limit}`);
  }

  getStatusBadge(status) {
    switch (status) {
      case ProductStatus.ALL:
        return { badge: 'blue' };
      case ProductStatus.TEST:
        return { badge: 'orange' };
      case ProductStatus.PENDING_REVIEW:
        return { badge: 'yellow' };
      case ProductStatus.ENTERED:
        return { badge: 'green' };
      case ProductStatus.DISABLED:
        return { badge: 'dark-grey' };
      case ProductStatus.END:
        return { badge: 'outline' };
      default:
        // 알 수 없는 상태는 dark-grey로 표시
        return { badge: 'dark-grey' };
    }
  }

  searchHashtag(search: HashtagSearchDto): Observable<Pagination<Hashtag>> {
    return this.apiService.post<Pagination<Hashtag>>(`/api/hashtags`, search);
  }

  /** 노출 가능한 테마 전체(그룹 순서 → 그룹 내 순서). 17건 고정 taxonomy라 페이지네이션이 없다. */
  searchTheme(): Observable<Theme[]> {
    // 17건 고정 taxonomy라 세션 중 바뀔 일이 없다. <theme-picker> 는 모달을 열 때마다 새로
    // 생성되므로 캐시가 없으면 상품을 연속 태깅할 때 같은 목록을 계속 다시 받는다.
    // 실패는 캐시하지 않는다 — 참조를 지워 다음 호출이 재시도하게 둔다.
    if (!this.themesCache$) {
      this.themesCache$ = this.apiService.get<Theme[]>(`/api/themes`).pipe(
        // 502 는 인터셉터가 EMPTY 로 삼켜 catchError 를 타지 않는다. 그대로 두면 값 없이 끝난
        // 스트림이 shareReplay 에 눌러앉아, 새로고침 전까지 칩도 오류 문구도 없는 백지가 된다.
        failIfSwallowed(),
        catchError((err) => {
          this.themesCache$ = undefined;
          // rxjs 6 에는 factory 오버로드가 없다. throwError(() => err) 로 쓰면
          // 에러 대신 화살표 함수가 흘러가 구독자의 err.message 가 undefined 가 된다.
          return throwError(err);
        }),
        shareReplay(1),
      );
    }
    return this.themesCache$;
  }

  searchAmenity(search: PaginationSearchDto): Observable<Pagination<Amenity>> {
    return this.apiService.get<Pagination<Amenity>>(`/api/products/search/amenity`, search);
  }

  canReservation(optionDate: string, reservationHours: number): boolean {
    const now = dayjs();
    const reservationTime = dayjs(optionDate).subtract(reservationHours, 'hour');
    const canReservation = now.isBefore(reservationTime);
    return canReservation;
  }

  confirmOrReject(dto: ProductConfirmCreateDto): Observable<ProductConfirm> {
    return this.apiService.post<ProductConfirm>('/api/product-confirm', dto);
  }

  getTotalViewsByMonth(year: number): Observable<
    {
      product: Product;
      data: { year: number; month: number; totalViews: number }[];
    }[]
  > {
    return this.apiService.post<
      {
        product: Product;
        data: { year: number; month: number; totalViews: number }[];
      }[]
    >(`/api/products/stat/total_views_by_month`, { year });
  }

  getRecommendSearchTerm(): Observable<{ id: number; term: string }[]> {
    return this.apiService.get<{ id: number; term: string }[]>(`/api/products/recommend_search_term`);
  }

  updateRecommendSearchTerm(value) {
    return this.apiService.post(`/api/products/recommend_search_term`, value);
  }

  getAvailableTicketDates(id: number): Observable<
    {
      date: Date;
      isClosed: boolean;
      isReservationEnd: boolean;
      product?: Product;
    }[]
  > {
    return this.apiService.get<
      {
        date: Date;
        isClosed: boolean;
        isReservationEnd: boolean;
        product?: Product;
      }[]
    >(`/api/products/tickets/dates/${id}`);
  }

  /** 서버의 오픈 게이트와 같은 판정식으로 계산한 "예약 가능한 일정 보유 여부"(NNB-679). */
  getHasReservableTicket(id: number): Observable<{ hasReservableTicket: boolean }> {
    return this.apiService.get<{ hasReservableTicket: boolean }>(`/api/products/${id}/has-reservable-ticket`);
  }

  getAdminTicketDates(productId: number): Observable<{ date: string }[]> {
    return this.apiService.get<{ date: string }[]>(`/api/products/tickets/admin/dates/${productId}`);
  }

  getAdminTicketsByDate(productId: number, date: string): Observable<Ticket[]> {
    return this.apiService.get<Ticket[]>(`/api/products/tickets/admin/product/${productId}/date/${date}`);
  }

  getTicketByDate(productId: number, date: string): Observable<Ticket[]> {
    return this.apiService.get<Ticket[]>(`/api/products/tickets/product/${productId}/date/${date}`);
  }

  addTickets(dto: TicketCreateBulkDto) {
    return this.apiService.post(`/api/products/tickets`, dto);
  }

  editTickets(groupId: number, dto: TicketCreateBulkDto) {
    return this.apiService.put(`/api/products/tickets/${groupId}`, dto);
  }

  getTicketBadges(search: PaginationSearchDto): Observable<Pagination<TicketBadge>> {
    return this.apiService.get<Pagination<TicketBadge>>(`/api/products/tickets/search/badge`, search);
  }

  searchTickets(search: TicketSearchDto): Observable<Ticket[]> {
    return this.apiService.get<Ticket[]>(`/api/products/tickets/search`, search);
  }

  getTicketGroups(
    productId: number,
    isOld: boolean,
    isActivated?: boolean,
    isDeactivated?: boolean,
  ): Observable<TicketGroup[]> {
    return this.apiService.post<TicketGroup[]>(`/api/products/tickets/groups`, {
      productId,
      isOld,
      isActivated,
      isDeactivated,
    });
  }

  getTicketGroup(groupId: number): Observable<TicketGroup> {
    return this.apiService.get<TicketGroup>(`/api/products/tickets/group/${groupId}`);
  }

  activateTicketGroup(groupId: number, isActivate: boolean) {
    return this.apiService.post(`/api/products/tickets/group/activate`, {
      groupId,
      isActivate,
    });
  }

  deleteTicketGroup(groupId: number) {
    return this.apiService.delete(`/api/products/tickets/group/${groupId}`);
  }

  patchTicketPricing(
    ticketId: number,
    data: {
      minParticipants?: number;
      maxParticipants?: number;
      pricePerPerson?: number;
      maxTeamCapacity?: number;
      minParticipantsPerTeam?: number;
      maxParticipantsPerTeam?: number;
      pricePerTeam?: number;
      extraPricePerPerson?: number;
      prices?: { quantity: number; price: number; discountPrice: number }[];
    },
  ) {
    return this.apiService.patch(`/api/products/tickets/${ticketId}`, data);
  }

  setReservationEndTicket(ticketId: number, isReservationEnd: boolean) {
    return this.apiService.post(`/api/products/tickets/reservationEnd`, {
      ticketId,
      isReservationEnd,
    });
  }

  setCloseTicket(productId: number, date: Date, isClosed: boolean) {
    return this.apiService.post(`/api/products/tickets/close`, {
      productId,
      date,
      isClosed,
    });
  }

  setCloseTicketRange(productId: number, from: Date, to: Date, isClosed: boolean) {
    return this.apiService.post<{
      from: string;
      to: string;
      isClosed: boolean;
      affectedTicketCount: number;
      closedDateCount: number;
    }>(`/api/products/tickets/close-range`, {
      productId,
      from,
      to,
      isClosed,
    });
  }

  hasReservationsInRange(productId: number, from: Date, to: Date) {
    return this.apiService.post<{ hasReservations: boolean }>(`/api/products/tickets/has-reservations-in-range`, {
      productId,
      from,
      to,
    });
  }

  getTicket(id: number) {
    return this.apiService.get<Ticket>(`/api/products/tickets/${id}`);
  }

  /**
   * 티켓 예약 가능 여부를 실시간으로 확인하는 경량 API
   * 결제 직전에 티켓 상태를 빠르게 검증하기 위해 사용
   */
  checkTicketAvailability(id: number): Observable<{ isAvailable: boolean; isReservationEnd: boolean }> {
    return this.apiService.get<{
      isAvailable: boolean;
      isReservationEnd: boolean;
    }>(`/api/products/tickets/${id}/availability`);
  }

  getTicketByGroup(groupId: number, isOld?: boolean) {
    return this.apiService.post<Ticket>(`/api/products/tickets/group`, {
      groupId,
      isOld,
    });
  }

  getCoupons(
    productId: number,
    downloadableNow?: boolean,
    ref?: string,
    userId?: number,
    targetDate?: string,
  ): Observable<Coupon[]> {
    return this.apiService
      .post<Coupon[]>(`/api/products/coupons/byProduct`, {
        productId,
        downloadableNow,
        ref,
        userId,
        targetDate,
      })
      .pipe(
        catchError((error) => {
          if (error.status === 0) {
            return of([]);
          }
          throw error;
        }),
      );
  }

  clone(id: number, linkReview?: boolean): Observable<Product> {
    return this.apiService.post<Product>(`/api/products/${id}/clone`, {
      linkReview,
    });
  }

  // ===== Refund Policy =====

  getRefundPoliciesRaw(productId: number): Observable<
    {
      id: number;
      daysBeforeStart: number;
      refundRate: number;
      reason: string;
    }[]
  > {
    return this.apiService.get(`/api/products/${productId}/refund-policies`);
  }

  updateRefundPolicies(
    productId: number,
    policies: {
      daysBeforeStart: number;
      refundRate: number;
      reason?: string;
    }[],
  ): Observable<any> {
    return this.apiService.put(`/api/products/${productId}/refund-policies`, {
      policies,
    });
  }

  // ===== Product Archive =====

  /**
   * 응답의 보관본을 화면이 쓰는 형태로 맞춘다.
   *
   * `createdAt` 은 JSON 을 거치며 문자열이 되므로 되살리고, `version` 은 목록 API 가 주기 전
   * 배포본에서 `undefined` 로 올 수 있어 `null`(모름)로 모아 둔다 — 소비자가 두 값을 따로
   * 신경 쓰지 않게 해야 파괴적 복원 경고의 판정이 한 갈래로 유지된다.
   */
  private toArchive(archive: ProductArchive): ProductArchive {
    const raw = archive.version != null ? archive.version : archive.data?.['version'];
    return { ...archive, createdAt: new Date(archive.createdAt), version: parseArchiveVersion(raw) };
  }

  createArchive(productId: number, name?: string, data?: ProductUpdateDto): Observable<ProductArchive> {
    return this.apiService
      .post<ProductArchive>(`/api/products/${productId}/archives`, createArchiveRequestBody(name, data))
      .pipe(map((archive) => this.toArchive(archive)));
  }

  getArchives(productId: number): Observable<ProductArchive[]> {
    return this.apiService
      .get<ProductArchive[]>(`/api/products/${productId}/archives`)
      .pipe(map((archives) => archives.map((archive) => this.toArchive(archive))));
  }

  /**
   * 보관본 1건을 본문(data)까지 받아온다. 목록(getArchives)은 용량 때문에 data 를 빼고 주므로,
   * 미리보기처럼 내용이 필요한 경우에만 이걸 쓴다.
   */
  getArchive(productId: number, archiveId: number): Observable<ProductArchive> {
    return this.apiService
      .get<ProductArchive>(`/api/products/${productId}/archives/${archiveId}`)
      .pipe(map((archive) => this.toArchive(archive)));
  }

  restoreArchive(productId: number, archiveId: number): Observable<Product> {
    return this.apiService.post<Product>(`/api/products/${productId}/archives/${archiveId}/restore`, {});
  }

  deleteArchive(productId: number, archiveId: number): Observable<void> {
    return this.apiService.delete<void>(`/api/products/${productId}/archives/${archiveId}`);
  }

  // Ticket Group Extension APIs
  validateExtensionToken(token: string): Observable<{
    isValid: boolean;
    ticketGroup?: any;
    jwt?: string;
    error?: string;
  }> {
    return this.apiService.get<{
      isValid: boolean;
      ticketGroup?: any;
      jwt?: string;
      error?: string;
    }>(`/api/ticket-groups/extension/validate/${token}`, undefined, {
      withCredentials: true, // Enable cookie setting for auto-login
    });
  }

  previewTicketGroupExtension(
    token: string,
    months: number,
  ): Observable<{
    newFrom: Date;
    newTo: Date;
    tickets: any[];
    totalTickets: number;
  }> {
    return this.apiService.get<{
      newFrom: Date;
      newTo: Date;
      tickets: any[];
      totalTickets: number;
    }>(`/api/ticket-groups/extension/preview/${token}?months=${months}`);
  }

  extendTicketGroup(
    token: string,
    extensionMonths: number,
    customEndDate?: string,
  ): Observable<{
    success: boolean;
    newTicketGroupId?: number;
    ticketsCreated?: number;
    error?: string;
  }> {
    const body: any = { token, extensionMonths };
    if (customEndDate) {
      body.customEndDate = customEndDate;
    }

    return this.apiService.post<{
      success: boolean;
      newTicketGroupId?: number;
      ticketsCreated?: number;
      error?: string;
    }>(`/api/ticket-groups/extension/extend`, body, {
      withCredentials: true, // Send JWT cookie for authentication
    });
  }
}
